import { Link, mergeStyles, MessageBar, MessageBarType, PrimaryButton, Spinner, Stack } from '@fluentui/react';
import * as React from 'react';
import { PageHeader } from '../../../../web/common/components/PageHeader';
import { StyledText } from '../../../../web/common/components/StyledText';
import { getHelpLogger } from '../../../../web/utils/getHelpLogger';
import { captureContentUpdate, capturePageAction } from '../../../../web/utils/oneDsUtil';
import { getSourceAppOrAppVerQueryString, getOmnichannelChatLinkTemplate, getOmnichannelContext } from '../../../../web/utils/statelessUtil';
import { getString } from '../../../../web/utils/stringLoader';
import { AlchemyResult } from '../../../models/apiModels';
import { Environment, SourceApps, URLs } from '../../../models/constants/constants';
import { ServiceRequestPageType } from '../../../models/reduxState';
import { sanitizeHTML, getUpsellMetaDataKey, isDfMRequest, isGetHelp, getRequestLocale } from '../../../utilities';
import { ExtensionAttributeKeys, FreelanceRequest, MetaDataKeys, RequestModality } from '../models/requestModels';
import { commonClassNames } from '../styles/commonStyles';
import { IContactUsStyles, getContactUsClassNames, getContactUsStyles } from '../styles/contactUsStyles';
import { getOpenItNowLinkStyle, getUpsellButtonStyles, requestConfirmationFooterText } from '../styles/modalityStyles';

import { IStyledComponent, IThemedProp } from '../styles/themesUI';
import { httpUtils } from '../../../../web/common/apis/httpUtils';
import { addLogs } from '../../../../../omnichannel-live-chat-widget/apis/emerald.apis';

interface RequestDetailsProps {
  request: FreelanceRequest,
  isOrgId: boolean,
  showModalitiesStickyFooter: boolean,
  upsellAgentAssistSubscriptionLink: string,
  omnichannelThemeName: string,
  isClientApp: boolean,
  openInAppOmnichannelChatInBrowser: boolean,
  market: string,
  isAmc: boolean,
  environment: Environment,
  sourceApp: string,
  appVer: string,
  correlationId: string;
}

const openChatLink = (
  environment: Environment,
  sourceApp: string,
  appVer: string,
  isClientApp: boolean,
  openInAppOmnichannelChatInBrowser: boolean,
  market: string,
  omnichannelThemeName: string,
  isOrgId: boolean,
  request: FreelanceRequest,
  correlationId: string
): void => {
  const useOmnichannel: boolean = isDfMRequest(request.RequestId);
  let chatLink = '';
  let openTarget = '_blank';

  if (useOmnichannel) {
    const ocLink = getOmnichannelChatLinkTemplate(environment, openInAppOmnichannelChatInBrowser);
    let requestLocale = getRequestLocale(request);
    chatLink = ocLink
      .replace('{0}', !!requestLocale ? requestLocale : market)
      .replace('{1}', omnichannelThemeName)
      .replace('{2}', getSourceAppOrAppVerQueryString(
        request.Source,
        isClientApp,
        openInAppOmnichannelChatInBrowser,
        appVer))
      .replace('{3}', encodeURIComponent(JSON.stringify(getOmnichannelContext(request))))
      .replace('{4}', httpUtils.getEmsSessionId());
  } else {
    chatLink = URLs.RaveChatWidgetLink
      .replace('{0}', request.Source)
      .replace('{1}', request.RequestId)
      .replace('{2}', isOrgId ? '&isaad=true' : '');
    if (request.Source === 'SignUp') {
      chatLink = URLs.UnAuthRaveChatWidgetLink
        .replace('{0}', request.Source)
        .replace('{1}', request.RequestId);
    }
  }

  if (isClientApp && !(useOmnichannel && openInAppOmnichannelChatInBrowser)
    || request.Source === 'SignUp'
    || request.Source === 'SmbHub'
    || request.Source === 'VsbHub'
    || sourceApp === SourceApps.Teams
    || sourceApp === SourceApps.Teams2
    || sourceApp === SourceApps.TeamsAdmin
    || sourceApp === SourceApps.TeamsAdmin2) {
    openTarget = '_self';
  }

  addLogs({
    event: 'OpenChatLinkFromRequestConfirmation',
    chatLink: chatLink,
    openTarget: openTarget,
  }, correlationId);

  window.open(chatLink, openTarget);
};

const RequestDetails = (props: RequestDetailsProps & IThemedProp) => {
  const onStartChatButtonClick = (event: React.MouseEvent<HTMLInputElement>): void => {
    if (!!event && !!event.target) {
      event.stopPropagation();
    }

    if (!props.request) {
      return null;
    }

    openChatLink(
      props.environment,
      props.sourceApp,
      props.appVer,
      props.isClientApp,
      props.openInAppOmnichannelChatInBrowser,
      props.market,
      props.omnichannelThemeName,
      props.isOrgId,
      props.request,
      props.correlationId
    );
  };

  // FreelanceRequest.Modality returned from the backend is number value but in string type, like '5'.
  const modalityType = props.request.Modality;
  const contactUsClassNames = getContactUsClassNames(getContactUsStyles, { theme: props.theme });
  const upsellButtonStyles = getUpsellButtonStyles({ theme: props.theme });
  const freelancerequest = { RequestId: props.request.RequestId, ParatureTicketNumber: props.request.ParatureTicketNumber };
  const contentUpdateLog = {
    content: {
      contentId: 'requestConfirmation',
      contentName: 'requestConfirmation',
      areaName: 'modalitiesSection',
      formnm: 'maxFunnel',
      freelancerequest: JSON.stringify(freelancerequest),
    },
    behavior: 160,
  };
  captureContentUpdate(contentUpdateLog);

  const handleAgentAssistSubscriptionLinkClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(props.upsellAgentAssistSubscriptionLink, '_blank');
  };

  return (
    <>
      <div className={contactUsClassNames.requestConfirmationProperty}>
        <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_titleLabel')}</div>
        <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.Title}</div>
      </div>
      {
        !props.isAmc &&
        (
          <div className={contactUsClassNames.requestConfirmationProperty}>
            <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_requestNumberLabel')}</div>
            <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.ParatureTicketNumber}</div>
          </div>
        )
      }
      {
        !props.isAmc &&
        (
          <div className={contactUsClassNames.requestConfirmationProperty}>
            <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_statusLabel')}</div>
            <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.State}</div>
          </div>
        )
      }
      <div className={contactUsClassNames.requestConfirmationProperty}>
        <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_nameLabel')}</div>
        <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.UserFirstName} {props.request.UserLastName}</div>
      </div>
      {
        // Do NOT use '==='.
        // modalityType returned from the backend is number value but string type like '4', so '===' will not work.
        (modalityType == RequestModality.Chat || modalityType == RequestModality.Email) &&
        (
          <div className={contactUsClassNames.requestConfirmationProperty}>
            <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_emailLabel')}</div>
            <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.UserEmail}</div>
          </div>
        )
      }
      {
        (modalityType == RequestModality.C2C || modalityType == RequestModality.Phone) &&
        (
          <div className={contactUsClassNames.requestConfirmationProperty}>
            <div className={contactUsClassNames.requestConfirmationPropertyLabel}>{getString('requestConfirmation_phoneNumberLabel')}</div>
            <div className={contactUsClassNames.requestConfirmationPropertyValue}>{props.request.UserPhone}</div>
          </div>
        )
      }
      {!props.showModalitiesStickyFooter && !props.isAmc && <hr className={contactUsClassNames.requestConfirmationHr} />}
      {
        (modalityType == RequestModality.Chat) &&
        !props.showModalitiesStickyFooter &&
        (props.sourceApp !== SourceApps.SignUp) && (
          <input
            id='requestConfirmationStartChatButton'
            type='button'
            className='btn btn-primary sharp'
            data-bi-bhvr='CONTACT'
            data-bi-area='modalitiesSection'
            data-bi-formnm='maxFunnel'
            data-bi-name='requestConfirmationStartChatButton'
            onClick={onStartChatButtonClick}
            value={getString('requestConfirmation_startChatButtonText')}
          />
        )
      }
      {
        (modalityType == RequestModality.Chat) && props.sourceApp === SourceApps.SignUp &&
        (
          <div>
            <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginBottom: '20px' }}>
              <Spinner styles={{ circle: { color: props.theme.palette.accent } }} />
              <span role="alert" aria-label={getString('requestConfirmation_signUpPageChatLoadingText')}>{getString('requestConfirmation_signUpPageChatLoadingText')}</span>
            </Stack>
          </div>
        )
      }
      {
        (modalityType == RequestModality.C2C) && !props.showModalitiesStickyFooter && !props.isAmc &&
        (
          <div className={contactUsClassNames.requestConfirmationMessageText}>{getString('requestConfirmation_c2CMessageText')}</div>
        )
      }
      {
        (modalityType == RequestModality.Phone) && !props.showModalitiesStickyFooter && !props.isAmc &&
        (
          <div className={contactUsClassNames.requestConfirmationMessageText}>{getString('requestConfirmation_phoneMessageText')}</div>
        )
      }
      {
        (modalityType == RequestModality.Email) &&
        (
          <div className={contactUsClassNames.requestConfirmationMessageText}>{getString('requestConfirmation_emailMessageText')}</div>
        )
      }
      {
        (getUpsellMetaDataKey(props.request) === MetaDataKeys.IsAgentAssistSubscriptionSignup) && !!props.upsellAgentAssistSubscriptionLink &&
        (
          <PrimaryButton
            id='upsell-agent-assist-subscribe-button'
            styles={upsellButtonStyles}
            data-bi-bhvr='CONTACT'
            data-bi-area='modalitiesSection'
            data-bi-name={`upsellSubscribeButton_${props.upsellAgentAssistSubscriptionLink.split('_').pop()}`}
            data-bi-formnm='maxFunnel'
            onClick={handleAgentAssistSubscriptionLinkClick}
            className={contactUsClassNames.upsellAgentAssistSubscribeButton}
            tabIndex={0}
          >
            {getString('m365Upsell_agentAssistSubscriptionLinkLabel')}
          </PrimaryButton>
        )
      }
    </>
  );
};
interface RequestConfirmationProps {
  results: AlchemyResult;
  isCreatingRequest: boolean;
  createdRequest: FreelanceRequest;
  isOrgId: boolean;
  autoOpenRaveChatRequestCorrelationId: string;
  updateRaveChatWindowRequestCorrelationId: (correlationId: string) => void;
  showModalitiesStickyFooter: boolean;
  isModalityThrottled: boolean;
  alchemyThemeRoutingEnabled: boolean;
  isOutlookDotCom: boolean;
  upsellAgentAssistSubscriptionLink: string;
  isClientApp: boolean;
  openInAppOmnichannelChatInBrowser: boolean;
  omnichannelThemeName: string;
  market: string;
  isAmc: boolean;
  environment: Environment;
  sourceApp: string;
  appVer: string;
  isSmbHub: boolean;
  headerTitle: string;
  alchemyThemeRoutingShowWaitTime: boolean;
  correlationId: string;

  resetSearchSession: () => void;
  resetUITabViewStack: () => void;
  navigateBack: (purpose?: ServiceRequestPageType) => void;
}

type IRequestConfirmationProps = RequestConfirmationProps & IStyledComponent<IContactUsStyles>;

export class RequestConfirmation extends React.Component<IRequestConfirmationProps> {
  public constructor(props: IRequestConfirmationProps) {
    super(props);
  }

  public componentDidMount = (): void => {
    if (
      !this.props.isCreatingRequest &&
      !!this.props.createdRequest
    ) {
      this.logAndOpenChatLink('componentDidMount');
    }
  };

  public componentDidUpdate = (prevProps: RequestConfirmationProps): void => {
    if (
      prevProps.isCreatingRequest &&
      !this.props.isCreatingRequest &&
      !!this.props.createdRequest
    ) {
      this.logAndOpenChatLink('componentDidUpdate');
    }
  };

  public getChatDescription = (props: RequestConfirmationProps) => {
    let chatDescriptionHtml: string = null;
    if (getString('requestConfirmation_chatShortly', true, true, true)) {
      chatDescriptionHtml = getString('requestConfirmation_chatShortly')
        .replace('{0}', `<a id='requestConfirmationOpenChatLink' role='link' data-bi-bhvr='CONTACT' data-bi-area='modalitiesSection' data-bi-formnm='maxFunnel' data-bi-name='requestConfirmationStartChatButton' tabindex='0'>`)
        .replace('{1}', '</a>');
    }
    return sanitizeHTML(chatDescriptionHtml);
  };

  public onKeyDownStartChat = (props: RequestConfirmationProps) => (event: React.KeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Enter') {
      event.stopPropagation();
      event.preventDefault();

      if (!this.props.createdRequest) {
        return;
      }

      openChatLink(
        props.environment,
        props.sourceApp,
        props.appVer,
        props.isClientApp,
        props.openInAppOmnichannelChatInBrowser,
        props.market,
        props.omnichannelThemeName,
        props.isOrgId,
        props.createdRequest,
        props.correlationId
      );
    }
  };

  public onStartChatClick = (props: RequestConfirmationProps) => (event: React.MouseEvent<HTMLInputElement>): void => {
    if (!!event && !!event.target) {
      event.stopPropagation();
    }

    if (!this.props.createdRequest) {
      return null;
    }

    openChatLink(
      props.environment,
      props.sourceApp,
      props.appVer,
      props.isClientApp,
      props.openInAppOmnichannelChatInBrowser,
      props.market,
      props.omnichannelThemeName,
      props.isOrgId,
      props.createdRequest,
      props.correlationId
    );
  };
  public render() {
    const contactUsClassNames = getContactUsClassNames(this.props.styles, { theme: this.props.theme });

    //  Show service request pending header if ticket creation is in process or failed
    let description = '';
    let descriptionElement = null;

    // modalityType returned from the backend is number value but string type like '4', so '===' will not work.
    if (this.props.createdRequest && this.props.createdRequest.Modality == RequestModality.Chat) {
      descriptionElement = isGetHelp() ? (
        <StyledText styles={{ root: { marginTop: '10px' } }}>
          {getString('TicketResultChatDescription1')}
          <Link
            href="#"
            styles={getOpenItNowLinkStyle(this.props.theme.palette.themeDarkAlt)}
            onClick={this.onStartChatClick(this.props)}
            onKeyDown={this.onKeyDownStartChat(this.props)}
            tabIndex={0}
          >
            {getString('TicketResultChatDescription2')}
          </Link>
        </StyledText>
      ) : (
        <div
          className={contactUsClassNames.sectionHeaderMainDescription}
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={this.getChatDescription(this.props)}
          onClick={this.onStartChatClick(this.props)}
          onKeyDown={this.onKeyDownStartChat(this.props)}
        />
      );
    } else if (this.props.createdRequest && (this.props.createdRequest.Modality == RequestModality.C2C || this.props.createdRequest.Modality == RequestModality.Phone)) {
      if (this.props.alchemyThemeRoutingShowWaitTime) {
        const extensionAttributes = this.props.createdRequest.ExtensionAttributes;
        if (extensionAttributes) {
          const waitTime = extensionAttributes[ExtensionAttributeKeys.WaitTime];
          if (waitTime !== null && waitTime !== undefined) {
            if (parseInt(waitTime, 10) === 1) {
              const expectACallDescription = getString('requestConfirmation_expectACallDescription');
              description = expectACallDescription ? expectACallDescription.replace('{0}', '1') : '';
            } else {
              const expectACallDescriptionPlural = getString('requestConfirmation_expectACallDescriptionPlural');
              description = expectACallDescriptionPlural ? expectACallDescriptionPlural.replace('{0}', waitTime) : '';
            }
          }
        }
      } else {
        const c2cRequestCreationDescription = getString('requestConfirmation_c2cRequestCreationDescription');
        const phoneRequestCreationDescription = getString('requestConfirmation_phoneRequestCreationDescription');
        const requestCreationDescription = (this.props.createdRequest && this.props.createdRequest.Modality == RequestModality.Phone) ? phoneRequestCreationDescription : c2cRequestCreationDescription;
        descriptionElement = requestCreationDescription
          ? <div className={contactUsClassNames.sectionHeaderMainDescription}>
            {requestCreationDescription}
          </div>
          : null;
      }
    }

    const body = (<div className={mergeStyles('col-lg-12', contactUsClassNames.col, contactUsClassNames.bodyCopySmall, contactUsClassNames.requestConfirmation, commonClassNames.overflowVisible)} id='request-confirmation-id'>
        {!this.props.showModalitiesStickyFooter && !this.props.isAmc && <div className={contactUsClassNames.requestConfirmationHeader}>{getString('requestConfirmation_headerText')}</div>}
        {this.props.isCreatingRequest ? (
          // Creating request message
          <Stack horizontal tokens={{ childrenGap: 8 }}>
            <Spinner styles={{ circle: { color: this.props.theme.palette.accent } }} />
            <span role="alert" aria-label={getString('requestConfirmation_creatingRequestMessageText')}>{getString('requestConfirmation_creatingRequestMessageText')}</span>
          </Stack>
        ) : (
          <>
            {!this.props.createdRequest ? (
              // Request creation failed
              <div>
                <MessageBar
                  messageBarType={MessageBarType.error}
                  isMultiline={false}
                >
                  {isGetHelp() ? (
                    this.getFailureMessageForGetHelp()
                  ) : (
                    <div
                      // eslint-disable-next-line react/no-danger
                      dangerouslySetInnerHTML={this.getFailureMessageHTML()}
                      onClick={this.onRequestCreationFailedRetryLinkClick}
                    />
                  )}
                </MessageBar>
              </div>
            ) : (
              // Request creation succeeded
              <RequestDetails
                request={this.props.createdRequest}
                isOrgId={this.props.isOrgId}
                showModalitiesStickyFooter={this.props.showModalitiesStickyFooter}
                upsellAgentAssistSubscriptionLink={this.props.upsellAgentAssistSubscriptionLink}
                theme={this.props.theme}
                omnichannelThemeName={this.props.omnichannelThemeName}
                isClientApp={this.props.isClientApp}
                openInAppOmnichannelChatInBrowser={this.props.openInAppOmnichannelChatInBrowser}
                market={this.props.market}
                isAmc={this.props.isAmc}
                environment={this.props.environment}
                sourceApp={this.props.sourceApp}
                appVer={this.props.appVer}
                correlationId={this.props.correlationId}
              />
            )}
            {isGetHelp() && (
              <StyledText styles={requestConfirmationFooterText}>
                {getString('TicketResultFooterMessage1')}
                <Link
                  styles={getOpenItNowLinkStyle(this.props.theme.palette.themeDarkAlt)}
                  onClick={this.onClickStartOver}
                >
                  {getString('TicketResultFooterMessage2')}
                </Link>
              </StyledText>
            )}
          </>
        )}
      </div>);

    return (
      <>
        <PageHeader title={this.props.headerTitle} description={description} descriptionElement={descriptionElement} theme={this.props.theme} />
        <div className={contactUsClassNames.panelBody}>
          {body}
        </div>
      </>
    );
  }

  private onClickStartOver = async () => {
    // await logDoneButtonFromTicketConfirmationPageClicked();
    this.props.resetSearchSession();
    this.props.resetUITabViewStack();

    // For entrypoints that don't have theme routing or in case of throttle error, link click should lead back to the modality stage
    if (!this.props.alchemyThemeRoutingEnabled || this.props.isModalityThrottled) {
      this.props.navigateBack(ServiceRequestPageType.ModalityOptions);
    } else {
      this.props.navigateBack(ServiceRequestPageType.AlchemyThemeRouting);
    }
  };

  private logAndOpenChatLink = (callerMethod: string): void => {
    if (
      !!this.props.createdRequest &&
      // Do NOT use '==='.
      // modalityType returned from the backend is number value but string type like '4', so '===' will not work.
      // Outlook and SmbHub don't have self-help so there is no props.results to check
      this.props.createdRequest.Modality == RequestModality.Chat &&
      (this.props.isOutlookDotCom || this.props.isSmbHub || !!this.props.results && this.props.autoOpenRaveChatRequestCorrelationId !==
        this.props.results.CorrelationId)
    ) {
      // log chat window opening
      const freelancerequest = {
        RequestId: this.props.createdRequest.RequestId,
        ParatureTicketNumber: this.props.createdRequest.ParatureTicketNumber,
      };
      const contentUpdateLog = {
        content: {
          contentId: 'requestConfirmationAutoChatOpen',
          contentName: 'requestConfirmationAutoChatOpen',
          areaName: 'modalitiesSection',
          callerMethod: callerMethod,
          formnm: 'maxFunnel',
          freelancerequest: JSON.stringify(freelancerequest),
        },
        behavior: 'CONTACT',
      };
      capturePageAction(null, contentUpdateLog);
      if (!this.props.isOutlookDotCom && !!this.props.results) {
        this.props.updateRaveChatWindowRequestCorrelationId(
          this.props.results.CorrelationId
        );
      }
      // open link
      openChatLink(
        this.props.environment,
        this.props.sourceApp,
        this.props.appVer,
        this.props.isClientApp,
        this.props.openInAppOmnichannelChatInBrowser,
        this.props.market,
        this.props.omnichannelThemeName,
        this.props.isOrgId,
        this.props.createdRequest,
        this.props.correlationId
      );
    }
  };

  private getFailureMessageHTML = () => {
    let failureMessageHTML: string = null;

    // Request creation failed due to modality being throttled
    if (this.props.isModalityThrottled) {
      failureMessageHTML = this.props.isAmc
        ? getString('requestConfirmation_errorAMCThrottleMessageText')
        : getString('requestConfirmation_errorThrottleMessageText')
          .replace('{0}', `<a data-bi-bhvr='CONTACT' data-bi-area='modalitiesSection' data-bi-name='requestCreationFailedThrottleLink' data-bi-formnm='maxFunnel' id='requestCreationFailedThrottleLink'>`)
          .replace('{1}', '</a>');
    } else if (!!getString('requestConfirmation_errorMessageText', true, true, true)) {
      failureMessageHTML = getString('requestConfirmation_errorMessageText')
        .replace('{0}', `<a data-bi-bhvr='CONTACT' data-bi-area='modalitiesSection' data-bi-name='requestCreationFailedRetryLink' data-bi-formnm='maxFunnel' id='requestCreationFailedRetryLink'>`)
        .replace('{1}', '</a>');
    }
    return sanitizeHTML(failureMessageHTML);
  };

  private getFailureMessageForGetHelp = (): JSX.Element => {
    getHelpLogger.logTicketErrorPageLoaded();
    const message = this.props.isModalityThrottled ? getString('requestConfirmation_errorThrottleMessageText') : getString('requestConfirmation_errorMessageText');
    const messageSegments = message.replace('{0}', '|').replace('{1}', '|').split('|');
    if (messageSegments.length === 3) {
      // the message is like "There was a problem submitting your support request. {0}Please try again.{1}"
      // messageSegments[0] = "There was a problem submitting your support request."
      // messageSegments[1] = "Please try again."
      // messageSegments[2] = ""
      return (
        <StyledText>
          {messageSegments[0]}
          <Link
            href="#"
            data-bi-bhvr="CONTACT"
            data-bi-area="modalitiesSection"
            data-bi-name={
              this.props.isModalityThrottled ? 'requestCreationFailedThrottleLink' : 'requestCreationFailedRetryLink'
            }
            data-bi-formnm="maxFunnel"
            id={this.props.isModalityThrottled ? 'requestCreationFailedThrottleLink' : 'requestCreationFailedRetryLink'}
            styles={getOpenItNowLinkStyle(this.props.theme.palette.themeDarkAlt)}
            onClick={this.onRequestCreationFailedRetryLinkClick}
          >
            {messageSegments[1]}
          </Link>
          {messageSegments[2]}
        </StyledText>
      );
    }
    return (
      <StyledText
        data-bi-bhvr="CONTACT"
        data-bi-area="modalitiesSection"
        data-bi-name={
          this.props.isModalityThrottled ? 'requestCreationFailedThrottleLink' : 'requestCreationFailedRetryLink'
        }
        data-bi-formnm="maxFunnel"
        id={this.props.isModalityThrottled ? 'requestCreationFailedThrottleLink' : 'requestCreationFailedRetryLink'}
        onClick={this.onRequestCreationFailedRetryLinkClick}
      >
        {message}
      </StyledText>
    );
  };

  private onRequestCreationFailedRetryLinkClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!!event) {
      const target = event.target as Element;
      if (!!target) {
        event.stopPropagation();
      }
      if (target && target.tagName === 'A') {
        // For entrypoints that don't have theme routing or in case of throttle error, link click should lead back to the modality stage
        if (!this.props.alchemyThemeRoutingEnabled || this.props.isModalityThrottled) {
          this.props.navigateBack(ServiceRequestPageType.ModalityOptions);
        } else {
          this.props.navigateBack(ServiceRequestPageType.AlchemyThemeRouting);
        }
      }
    }
  };
}
